import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS as cssDndKit } from '@dnd-kit/utilities';
import { LayerGroupIcon, PushpinEmptyIcon, PushpinIcon } from '@panwds/icons';
import { Checkbox, Tooltip } from '@panwds/react-ui';
import { classNames } from '@panwds/tailwindcss';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { arrayMoveImmutable } from '../../utils/arrayMove.js';
import { DragHandle } from '../drag_handle.js';
const List = (props) => (React.createElement("ul", { className: "twds-box-border twds-m-0 twds-p-0 twds-border-0", ...props }));
function Item({ column, disableColumnOrder = false, itemIndex, leftStickyItems, isDragging, setLeftStickyItems, rightStickyItems, setRightStickyItems, setSortableItems, sortableItems, setColumnOrder }) {
    const isGrouped = column.isGrouped;
    const isVisible = column.isVisible;
    const canMoveColumn = !isGrouped && !disableColumnOrder;
    /* istanbul ignore next */
    const leftPinCount = leftStickyItems.length === 2 ? 0 : leftStickyItems.length === 0 ? 2 : 1;
    const rightPinCount = rightStickyItems.length;
    const intl = useIntl();
    const { setNodeRef, transform, transition, listeners } = useSortable({
        id: column.id
    });
    const style = {
        transform: cssDndKit.Transform.toString(transform),
        transition
    };
    const label = isVisible
        ? intl.formatMessage({
            defaultMessage: 'Hide column {column}',
            id: '62Xtv/',
            description: 'Label for hiding column'
        }, {
            column: column.id
        })
        : intl.formatMessage({
            defaultMessage: 'Show column {column}',
            id: 'l+yPoX',
            description: 'Label for showing column'
        }, {
            column: column.id
        });
    return (React.createElement("li", { key: `item${column.id}`, className: classNames('twds-box-border twds-flex twds-text-xs twds-space-x-1 twds-items-center twds-h-10 twds-py-1 twds-px-2 twds-border-b last:twds-border-b-0 twds-w-full twds-bg-white dark:twds-border-gray-950 dark:twds-bg-gray-1050 hover:twds-bg-blue-200 active:twds-bg-blue-100 dark:hover:twds-bg-gray-800 dark:active:twds-bg-gray-900', isDragging && 'twds-z-20'), style: style, ref: setNodeRef },
        React.createElement("div", { className: "twds-w-6" },
            itemIndex + 1 <= leftPinCount && !isGrouped && (React.createElement(Tooltip, { label: intl.formatMessage({
                    defaultMessage: 'Pin Column Left',
                    id: '986psi',
                    description: 'Label for column left pin'
                }) },
                React.createElement("button", { type: "button", "data-testid": `left-pin-${itemIndex + 1}-button`, className: "twds-border-none twds-bg-transparent twds-box-border twds-mt-1 twds-mx-0 twds-mb-0 twds-p-0 twds-flex twds-items-center", "aria-label": intl.formatMessage({
                        defaultMessage: 'Pin column to the left',
                        id: 'OTKUWu',
                        description: 'Label for pinning column to the left'
                    }), onClick: () => {
                        let newColumnOrder = [''];
                        if (column.sticky === false || column.sticky === undefined) {
                            column.sticky = true;
                            column.stickyPosition = 'left';
                            setSortableItems(sortableItems.filter((e) => e !== column.id));
                            setLeftStickyItems(leftStickyItems.push(column.id));
                            leftStickyItems.map((item) => {
                                newColumnOrder.push(item);
                            });
                            newColumnOrder.concat(sortableItems);
                            rightStickyItems.map((item) => {
                                newColumnOrder.push(item);
                            });
                            newColumnOrder = newColumnOrder.filter((item) => {
                                return item !== '';
                            });
                            setColumnOrder(newColumnOrder);
                        }
                    } },
                    React.createElement(PushpinEmptyIcon, { size: "sm", className: "twds-text-gray-600 dark:twds-text-dark-bg" })))),
            rightPinCount === 0 && itemIndex + 1 === sortableItems.length && sortableItems.length > 2 && !isGrouped && (React.createElement(Tooltip, { label: intl.formatMessage({
                    defaultMessage: 'Pin Column Right',
                    id: 'PWs95n',
                    description: 'Label for column right pin'
                }) },
                React.createElement("button", { type: "button", "data-testid": "right-pin-button", className: "twds-border-none twds-bg-transparent twds-box-border twds-mt-1 twds-mx-0 twds-mb-0 twds-p-0 twds-flex twds-items-center", "aria-label": intl.formatMessage({
                        defaultMessage: 'Pin column to the right',
                        id: 'FPIEWZ',
                        description: 'Label for pinning column to the right'
                    }), onClick: () => {
                        let newColumnOrder = [''];
                        if (column.sticky === false || column.sticky === undefined) {
                            column.sticky = true;
                            column.stickyPosition = 'right';
                            setSortableItems(sortableItems.filter((e) => e !== column.id));
                            setRightStickyItems(rightStickyItems.concat(column.id));
                            newColumnOrder.concat(leftStickyItems);
                            sortableItems.map((item) => {
                                newColumnOrder.push(item);
                            });
                            newColumnOrder.concat(rightStickyItems);
                            newColumnOrder = newColumnOrder.filter((item) => {
                                return item !== '';
                            });
                            setColumnOrder(newColumnOrder);
                        }
                    } },
                    React.createElement(PushpinEmptyIcon, { size: "sm", className: "twds-text-gray-600 dark:twds-text-dark-bg" }))))),
        React.createElement(Tooltip, { label: label },
            React.createElement(Checkbox, { addClassName: 'twds-flex-none twds-mr-1', "aria-label": label, disabled: !!isGrouped, checked: isVisible, onChange: () => {
                    column.toggleHidden();
                } })),
        React.createElement("span", { className: "twds-flex-auto" }, column.render('Header')),
        React.createElement("span", { className: "twds-flex twds-space-x-1 twds-flex-none" },
            isGrouped && (React.createElement(Tooltip, { label: intl.formatMessage({
                    defaultMessage: 'Column is grouped',
                    id: 'yIVIsx',
                    description: 'Label for grouped column'
                }) },
                React.createElement("span", { "aria-label": intl.formatMessage({
                        defaultMessage: 'Column is grouped',
                        id: 'yIVIsx',
                        description: 'Label for grouped column'
                    }), className: "twds-p-1 twds-mr-0.5 twds-text-gray-600" },
                    React.createElement(LayerGroupIcon, null)))),
            canMoveColumn && (React.createElement(DragHandle, { label: intl.formatMessage({
                    defaultMessage: 'Drag to change column order',
                    id: 'Shgeg+',
                    description: 'Reorder column'
                }), addClassName: "twds-flex-none twds-cursor-move focus:twds-outline-none focus:twds-ring twds-rounded twds-text-icon-gray", ...listeners })))));
}
export function ColumnList({ columns, columnOrder, disableColumnOrder, setColumnOrder }) {
    // const SortableItem = useMemo(() => SortableElement<ItemProps<D>>(Item), [])
    const columnMap = useMemo(() => columns.reduce((acc, c) => {
        acc[c.id] = c;
        return acc;
    }, {}), [columns]);
    const [leftStickyItems, setLeftStickyItems] = useState([]);
    const [rightStickyItems, setRightStickyItems] = useState([]);
    const [sortableItems, setSortableItems] = useState([]);
    const filteredColumnOrder = columnOrder.filter((cId) => columnMap[cId]);
    columns.forEach((column) => !filteredColumnOrder.includes(column.id) && filteredColumnOrder.push(column.id));
    const groupedItems = filteredColumnOrder.filter((cId) => columnMap[cId].isGrouped);
    const intl = useIntl();
    const sensors = useSensors(useSensor(MouseSensor, {
        // Require the mouse to move by 10 pixels before activating.
        // Slight distance prevents sortable logic messing with
        // interactive elements in the handler toolbar component.
        activationConstraint: {
            distance: 10
        }
    }), useSensor(TouchSensor, {
        // Press delay of 250ms, with tolerance of 5px of movement.
        activationConstraint: {
            delay: 250,
            tolerance: 5
        }
    }));
    const [draggedColumnId, setDraggedColumnId] = useState(null);
    const getIndex = useCallback((id) => sortableItems.findIndex((c) => c === id), [sortableItems]);
    useEffect(() => {
        setLeftStickyItems(filteredColumnOrder.filter((cId) => columnMap[cId].sticky && columnMap[cId].stickyPosition === 'left'));
        setRightStickyItems(filteredColumnOrder.filter((cId) => columnMap[cId].sticky && columnMap[cId].stickyPosition === 'right'));
        setSortableItems(filteredColumnOrder.filter((cId) => !columnMap[cId].isGrouped && !columnMap[cId].sticky));
    }, [columnOrder]);
    /* istanbul ignore next */
    function onSortEnd({ oldIndex, newIndex }) {
        const next = groupedItems
            .concat(leftStickyItems ? leftStickyItems : '')
            .concat(arrayMoveImmutable(sortableItems, oldIndex, newIndex))
            .concat(rightStickyItems ? rightStickyItems : '');
        setColumnOrder(next);
    }
    return (React.createElement("div", { className: "twds-flex-auto twds-overflow-auto twds-scrollbar-default dark:twds-scrollbar-track-dark dark:twds-scrollbar-thumb-dark dark:twds-scrollbar-corner-dark" },
        !!groupedItems.length && (React.createElement(List, { className: "twds-box-border twds-m-0 twds-p-0 twds-border-0" }, groupedItems.map((cId, index) => {
            /* istanbul ignore next */
            if (columnMap[cId] === undefined)
                return null;
            return (React.createElement(Item, { key: cId, itemIndex: index, leftStickyItems: leftStickyItems, setLeftStickyItems: setLeftStickyItems, rightStickyItems: rightStickyItems, setRightStickyItems: setRightStickyItems, sortableItems: sortableItems, setSortableItems: setSortableItems, setColumnOrder: setColumnOrder, column: columnMap[cId] }));
        }))),
        !!leftStickyItems.length && (React.createElement("ul", { className: "twds-box-border twds-m-0 twds-p-0 twds-border-0" }, leftStickyItems.map((cId, itemIndex) => {
            if (columnMap[cId] === undefined)
                return null;
            const label = columnMap[cId].isVisible
                ? intl.formatMessage({
                    defaultMessage: 'Hide column {column}',
                    id: '62Xtv/',
                    description: 'Label for hiding column'
                }, {
                    column: columnMap[cId].id
                })
                : intl.formatMessage({
                    defaultMessage: 'Show column {column}',
                    id: 'l+yPoX',
                    description: 'Label for showing column'
                }, {
                    column: columnMap[cId].id
                });
            return (React.createElement("li", { key: `item${cId}`, className: "twds-box-border twds-flex twds-text-xs twds-space-x-1 twds-items-center twds-h-10 twds-py-1 twds-px-2 twds-border-b last:twds-border-b-0 twds-w-full twds-bg-white dark:twds-border-gray-950 dark:twds-bg-gray-1050 hover:twds-bg-blue-200 active:twds-bg-blue-100 dark:hover:twds-bg-gray-800 dark:active:twds-bg-gray-900" },
                React.createElement("div", { className: "twds-w-6" },
                    React.createElement(Tooltip, { label: intl.formatMessage({
                            defaultMessage: 'Unpin Column Left',
                            id: 'fuTLZx',
                            description: 'Label for column left unpinning'
                        }) },
                        React.createElement("button", { type: "button", "data-testid": `left-unpin-${itemIndex + 1}-button`, className: "twds-border-none twds-bg-transparent twds-box-border twds-mt-1 twds-mx-0 twds-mb-0 twds-p-0 twds-flex twds-items-center", "aria-label": intl.formatMessage({
                                defaultMessage: 'Column pinned to the left',
                                id: '3uI9TH',
                                description: 'Label for column left pinned'
                            }), onClick: () => {
                                let newSortableItems = [];
                                if (columnMap[cId].sticky === true) {
                                    columnMap[cId].sticky = false;
                                    columnMap[cId].stickyPosition = undefined;
                                    setLeftStickyItems(leftStickyItems.filter((e) => e !== columnMap[cId].id));
                                    newSortableItems.push(columnMap[cId].id);
                                    sortableItems.map((item) => {
                                        newSortableItems.push(item);
                                    });
                                    newSortableItems = newSortableItems.filter((item) => {
                                        return item !== '';
                                    });
                                    setSortableItems(newSortableItems);
                                }
                            } },
                            React.createElement(PushpinIcon, { size: "sm", className: "twds-text-blue-600 dark:twds-text-blue-400" })))),
                React.createElement(Tooltip, { label: label },
                    React.createElement(Checkbox, { addClassName: 'twds-flex-none twds-mr-1', "aria-label": label, checked: columnMap[cId].isVisible, onChange: () => {
                            columnMap[cId].toggleHidden();
                        } })),
                React.createElement("span", { className: "twds-flex-auto" }, columnMap[cId].render('Header'))));
        }))),
        React.createElement(DndContext, { sensors: sensors, autoScroll: false, onDragStart: ({ active }) => {
                if (active) {
                    setDraggedColumnId(String(active.id));
                }
            }, onDragEnd: ({ active, over }) => {
                if (over && active.id !== over.id) {
                    onSortEnd({
                        oldIndex: getIndex(active.id),
                        newIndex: getIndex(over.id)
                    });
                }
            } },
            React.createElement(SortableContext, { items: sortableItems, strategy: verticalListSortingStrategy }, sortableItems.map((cId, index) => {
                if (columnMap[cId] === undefined)
                    return null;
                return (React.createElement(Item, { key: cId, column: columnMap[cId], disableColumnOrder: disableColumnOrder, itemIndex: index, isDragging: draggedColumnId === cId, leftStickyItems: leftStickyItems, setLeftStickyItems: setLeftStickyItems, rightStickyItems: rightStickyItems, setRightStickyItems: setRightStickyItems, setSortableItems: setSortableItems, sortableItems: sortableItems, setColumnOrder: setColumnOrder }));
            }))),
        !!rightStickyItems.length && (React.createElement("ul", { className: "twds-box-border twds-m-0 twds-p-0 twds-border-0" }, rightStickyItems.map((cId) => {
            if (columnMap[cId] === undefined)
                return null;
            const label = columnMap[cId].isVisible
                ? intl.formatMessage({
                    defaultMessage: 'Hide column {column}',
                    id: '62Xtv/',
                    description: 'Label for hiding column'
                }, {
                    column: columnMap[cId].id
                })
                : intl.formatMessage({
                    defaultMessage: 'Show column {column}',
                    id: 'l+yPoX',
                    description: 'Label for showing column'
                }, {
                    column: columnMap[cId].id
                });
            return (React.createElement("li", { key: `item${cId}`, className: "twds-box-border twds-flex twds-text-xs twds-space-x-1 twds-items-center twds-h-10 twds-py-1 twds-px-2 twds-border-b last:twds-border-b-0 twds-w-full twds-bg-white dark:twds-border-gray-950 dark:twds-bg-gray-1050 hover:twds-bg-blue-200 active:twds-bg-blue-100 dark:hover:twds-bg-gray-800 dark:active:twds-bg-gray-900" },
                React.createElement("div", { className: "twds-w-6" },
                    React.createElement(Tooltip, { label: intl.formatMessage({
                            defaultMessage: 'Unpin Column Right',
                            id: 'wVvPRt',
                            description: 'Label for column right unpinning'
                        }) },
                        React.createElement("button", { type: "button", "data-testid": "right-unpin-button", className: "twds-border-none twds-bg-transparent twds-box-border twds-mt-1 twds-mx-0 twds-mb-0 twds-p-0 twds-flex twds-items-center", "aria-label": intl.formatMessage({
                                defaultMessage: 'Column pinned to the right',
                                id: 'eUOtzV',
                                description: 'Label for column right pinned'
                            }), onClick: () => {
                                let newSortableItems = [];
                                if (columnMap[cId].sticky === true) {
                                    columnMap[cId].sticky = false;
                                    columnMap[cId].stickyPosition = undefined;
                                    setRightStickyItems(rightStickyItems.filter((e) => e !== columnMap[cId].id));
                                    sortableItems.map((item) => {
                                        newSortableItems.push(item);
                                    });
                                    newSortableItems.push(columnMap[cId].id);
                                    newSortableItems = newSortableItems.filter((item) => {
                                        return item !== '';
                                    });
                                    setSortableItems(newSortableItems);
                                }
                            } },
                            React.createElement(PushpinIcon, { size: "sm", className: "twds-text-blue-600 dark:twds-text-blue-400" })))),
                React.createElement(Tooltip, { label: label },
                    React.createElement(Checkbox, { "data-testid": "hide-show-column-checkbox", addClassName: 'twds-flex-none twds-mr-1', "aria-label": label, checked: columnMap[cId].isVisible, onChange: () => {
                            columnMap[cId].toggleHidden();
                        } })),
                React.createElement("span", { className: "twds-flex-auto" }, columnMap[cId].render('Header'))));
        })))));
}
//# sourceMappingURL=_column_list.js.map